// Recently viewed products — local-only
//
// We track a small ring buffer of product handles in localStorage. Pages call
// `track(handle)` from a useEffect on the product page; UI surfaces (home,
// wishlist, PDP) read the list with `useRecentlyViewed()`.

import { useCallback, useEffect, useState } from "react";
import { hasConsent } from "@/lib/consent";

const KEY = "fl:recent:v1";
const MAX = 12;

type Listener = (handles: string[]) => void;
const listeners = new Set<Listener>();

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string").slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function write(handles: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(handles));
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l(handles));
}

/** Push a handle to the front. No-op on server. */
export function trackRecentlyViewed(handle: string) {
  if (!handle || typeof window === "undefined" || !hasConsent("preferences")) return;
  const current = read();
  const next = [handle, ...current.filter((h) => h !== handle)].slice(0, MAX);
  write(next);
}

export function useRecentlyViewed(exclude?: string) {
  const [handles, setHandles] = useState<string[]>([]);

  useEffect(() => {
    const onChange: Listener = (next) => setHandles(next);
    listeners.add(onChange);
    setHandles(read());
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setHandles(read());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const list = exclude ? handles.filter((h) => h !== exclude) : handles;

  const clear = useCallback(() => write([]), []);
  return { handles: list, clear };
}
